import { Button, FormControlLabel, FormGroup, Grid, IconButton, Paper, Stack, Switch, Tab, Tabs, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../store/store';
import { RootState } from '../../reducers';
import { closeEditHandler, handleSaveRole, setAssignedAssessments, setSelectAllAssessments } from '../../store/adminRoles.slice';

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { copyObject } from '../../utils/common';
import { assessmentMenuEnum } from '../../utils/assessments';
import { AssignedAssessments } from '../../Models/Role/AssignedAssessments.model';

function a11yProps(index: any) {
    return {
        id: `vertical-tab-${index}`,
        'aria-controls': `vertical-tabpanel-${index}`,
    };
}

const AssessmentsByRole = (props) => {
    const dispatch = useAppDispatch();
    const { assessmentsByRole, assignedAssessments, openNew, selectedRole, selectAllAssessments } = useSelector((state: RootState) => state.adminRoles);

    const startingAssessments = [assessmentMenuEnum.CONSENT];

    const endingAssessments = [
        assessmentMenuEnum.PROCEDURE_SIGN_OFF,
        assessmentMenuEnum.SUMMARY_NOTES,
        assessmentMenuEnum.SET_APPOINTMENTS,
        assessmentMenuEnum.PROGRESS_NOTES,
        assessmentMenuEnum.HRA_EDUCATION,
        assessmentMenuEnum.FOLLOW_UP,
    ];
    const [selectedAssessmentTab, setSelectedAssessmentTab] = React.useState<string>(assignedAssessments.length > 0 ? assignedAssessments[0].Id : '');

    const handleSelectAssessmentTab = (event: React.SyntheticEvent, newValue: string) => {
        setSelectedAssessmentTab(newValue);
    };

    const selectAllHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            dispatch(setSelectAllAssessments(true));
            dispatch(setAssignedAssessments([...assessmentsByRole]));
        } else {
            dispatch(setSelectAllAssessments(false));
            dispatch(setAssignedAssessments([]));
        }
    };

    const roleAssessmentHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const existingIndex = assignedAssessments.findIndex((perm) => perm.Name === e.target.value);
        if (existingIndex === -1) {
            const assessment = assessmentsByRole.find((perm) => perm.Id === e.target.id);
            if (!assessment) return;
            let assessmentCopy = copyObject(assessment);
            const newAssignedAssessments = copyObject(assignedAssessments);
            if (endingAssessments.includes(e.target.value)) {
                newAssignedAssessments.push(assessmentCopy);
                newAssignedAssessments.forEach((item, index) => {
                    item.Order = index + 1;
                });
            } else {
                newAssignedAssessments.splice(1, 0, assessmentCopy);
                newAssignedAssessments.forEach((item, index) => {
                    item.Order = index + 1;
                });
            }
            dispatch(setAssignedAssessments(newAssignedAssessments));
        } else {
            dispatch(setAssignedAssessments(assignedAssessments.filter((perm) => perm.Id !== e.target.id)));
        }
    };

    const renderTabLable = (menu: AssignedAssessments, index: Number) => {
        return (
            <div className="menu-item" key={`assessment-menu-item-${index}`}>
                <ArrowForwardIosIcon fontSize="small" className="arrow-icon" />
                <span className="fill">{menu.Name}</span>
            </div>
        );
    };

    const menuList = [...assignedAssessments].sort((a, b) => (a.Order || 0) - (b.Order || 0));

    const increaseOrderHandler = () => {
        const newList = copyObject(menuList);
        const currentIndex = newList.findIndex((el) => el.Id === selectedAssessmentTab);
        const currentAssessment = newList[currentIndex];
        const assessmentAfterCurrent = newList[currentIndex + 1];
        const endOfList = endingAssessments.includes(assessmentAfterCurrent.Name) || !assessmentAfterCurrent;
        if (endOfList) {
            return;
        } else {
            const oldOrder = currentAssessment.Order;
            const newOrder = assessmentAfterCurrent.Order;
            currentAssessment.Order = newOrder;
            assessmentAfterCurrent.Order = oldOrder;
            dispatch(setAssignedAssessments(newList));
        }
    };

    const decreaseOrderHandler = () => {
        const newList = copyObject(menuList);
        const currentIndex = newList.findIndex((el) => el.Id === selectedAssessmentTab);
        const currentAssessment = newList[currentIndex];
        const assessmentBeforeCurrent = newList[currentIndex - 1];
        if (startingAssessments.includes(assessmentBeforeCurrent.Name) || !assessmentBeforeCurrent) {
            return;
        } else {
            const oldOrder = currentAssessment.Order;
            const newOrder = assessmentBeforeCurrent.Order;
            currentAssessment.Order = newOrder;
            assessmentBeforeCurrent.Order = oldOrder;
            dispatch(setAssignedAssessments(newList));
        }
    };

    const isDecreaseOrderDisabledHandler = () => {
        const selectedAssessment = assignedAssessments.find((el) => el.Id === selectedAssessmentTab);
        const currentIndex = menuList.findIndex((el) => el.Id === selectedAssessmentTab);
        const firstItemInMenuList = menuList[0];
        const isDisabled =
            selectedAssessment?.Order === firstItemInMenuList.Order ||
            currentIndex === -1 ||
            menuList.length === 0 ||
            endingAssessments.includes(selectedAssessment.Name);
        return isDisabled;
    };

    const isIncreaseOrderDisabledHandler = () => {
        const selectedAssessment = assignedAssessments.find((el) => el.Id === selectedAssessmentTab);
        const currentIndex = menuList.findIndex((el) => el.Id === selectedAssessmentTab);
        const lastItemInMenuList = menuList[menuList.length - 1];
        const isDisabled =
            selectedAssessment?.Order === lastItemInMenuList.Order ||
            currentIndex === -1 ||
            menuList.length === 0 ||
            startingAssessments.includes(selectedAssessment.Name);
        return isDisabled;
    };

    const renderAssessmentPreview = () => {
        return (
            <Grid size={3} pr={3} display="flex" flexDirection={'column'} alignItems="center">
                <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', fontStyle: 'italic', color: 'text.secondary' }}>
                    Assessment Menu Preview
                </Typography>
                <Stack direction="row" alignItems={'center'} spacing={2}>
                    <Stack direction="column" spacing={2}>
                        <IconButton disabled={isDecreaseOrderDisabledHandler()} onClick={() => decreaseOrderHandler()}>
                            <KeyboardArrowUpIcon />
                        </IconButton>
                        <IconButton disabled={isIncreaseOrderDisabledHandler()} onClick={() => increaseOrderHandler()}>
                            <KeyboardArrowDownIcon />
                        </IconButton>
                    </Stack>
                    <Paper elevation={3} sx={{ p: 2 }}>
                        <div className="assessment-tab">
                            <Tabs
                                orientation="vertical"
                                value={selectedAssessmentTab || (menuList.length > 0 ? menuList[0].Id : '')}
                                onChange={handleSelectAssessmentTab}
                            >
                                {menuList.map((menu, index) => (
                                    <Tab
                                        key={menu.Id}
                                        disabled={endingAssessments.includes(menu.Name) || startingAssessments.includes(menu.Name)}
                                        id={menu.Id}
                                        value={menu.Id}
                                        label={renderTabLable(menu, index)}
                                        {...a11yProps(menu.Name)}
                                    />
                                ))}
                            </Tabs>
                        </div>
                    </Paper>
                </Stack>
            </Grid>
        );
    };

    useEffect(() => {
        if (assignedAssessments.length === assessmentsByRole.length) {
            dispatch(setSelectAllAssessments(true));
        } else {
            dispatch(setSelectAllAssessments(false));
        }
    }, [assignedAssessments.length, assessmentsByRole.length, dispatch]);

    return (
        <Grid size={12}>
            <Grid container rowSpacing={2} columnSpacing={3}>
                <Grid size={12}>
                    <FormControlLabel
                        disabled={selectedRole.Name.length === 0 && !openNew ? true : false}
                        label="Select All Assessments"
                        control={<Switch checked={selectAllAssessments} onChange={selectAllHandler} />}
                    />
                </Grid>

                <Grid size={12}>
                    <Grid container rowSpacing={2} columnSpacing={3}>
                        <Grid size={9}>
                            <FormGroup sx={{ padding: 2, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                                {assessmentsByRole.map((el) => {
                                    return (
                                        <Grid size={4} key={el.Id}>
                                            <FormControlLabel
                                                label={el.Name}
                                                control={
                                                    <Switch
                                                        id={el.Id}
                                                        checked={assignedAssessments.map((i) => i.Id).includes(el.Id)}
                                                        disabled={selectedRole.Name.length === 0 && !openNew ? true : false}
                                                        value={el.Name}
                                                        onChange={roleAssessmentHandler}
                                                    />
                                                }
                                            />
                                        </Grid>
                                    );
                                })}
                            </FormGroup>
                        </Grid>
                        {menuList.length > 0 && renderAssessmentPreview()}
                    </Grid>
                </Grid>

                <Grid size={12} display="flex" justifyContent={'flex-end'} mt={2}>
                    <Button
                        className="button-120"
                        sx={{ marginRight: 2 }}
                        disabled={selectedRole.Name.length === 0 && !openNew ? true : false}
                        variant="contained"
                        onClick={() => dispatch(handleSaveRole())}
                    >
                        Save
                    </Button>
                    <Button className="button-120" variant="contained" onClick={() => dispatch(closeEditHandler())}>
                        Cancel
                    </Button>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default AssessmentsByRole;
